// tableSidePanel.tsx
import './tableSidepanel.css';
import React, { useState, useEffect } from 'react';
import { ChevronsRight as ChevronsRightIcon } from 'lucide-react';
import ScheduleSidePanel from './schedule_sidepanel/scheduleSidepanel';
import PricingSidePanel from './pricing_sidepanel/pricingSidepanel';
import VacTruckSidePanel from './vac_truck_sidepanel/VacTruckSidePanel';
import FirebaseSidePanel from './firebase_sidepanel/firebaseSidepanel';
import ColumnConfigSidePanel from './column_configuration_sidepanel/columnConfigSidepanel';
import { SQLTableProps } from '../tableMain';
import type { TableColumnConfig } from '../tableMain';

interface TableSidePanel {
    tableType: SQLTableProps['tableType'];
    tableName: string;
    sourceTableName: string;
    isOpen: boolean;
    onClose: () => void;
    panelData?: {
        rowData: any;
        colName: string;
        tableName: string;
    };
    columnConfigs: TableColumnConfig[];
}

const TableSidePanel: React.FC<TableSidePanel> = ({
    tableType,
    tableName,
    sourceTableName,
    isOpen,
    onClose,
    panelData,
    columnConfigs,
}) => {

    const [isAnimating, setIsAnimating] = useState(false);
    const [isAnyItemEditing, setIsAnyItemEditing] = useState(false);

    // key listener for closing on Escape key press
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !isAnyItemEditing) {
                handleClose();
            }
        };

        // Add event listener when panel is open
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }

        // Cleanup function to remove event listener
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen, isAnyItemEditing]);

    // give the panel time before closing so it can animate before its removed from the DOM
    const handleClose = () => {
        setIsAnimating(true);
        // Wait for animation to complete before actually closing
        setTimeout(() => {
            onClose();
            setIsAnimating(false);
        }, 150); // Match the CSS animation duration
    };

    if (!isOpen) return null;

    return (
        <div className={`tableSidePanel-mainContainer ${isAnimating ? 'closing' : ''}`}>
            <div className="tableSidePanel-header">
                <div>
                    <button
                        className="tableSidePanel-closeButton"
                        onClick={handleClose}
                    >
                        <ChevronsRightIcon 
                            size={20} 
                            style={{color: 'var(--color-grey-light-text)'}}
                        />
                    </button>
                </div>
            </div>
            <div className="tableSidePanel-dividingLine"/>
            {tableType === 'firebase' && (
                <FirebaseSidePanel
                    isOpen={isOpen}
                    rowData={panelData?.rowData ?? null}
                    onClose={handleClose}
                />
            )}
            {tableName === 'schedule_table' && (
                <ScheduleSidePanel
                    isOpen={isOpen}
                    panelData={panelData}
                    onEditingStateChange={setIsAnyItemEditing}
                />
            )}
            {tableName === 'carrier_pricing' && (
                <PricingSidePanel
                    isOpen={isOpen}
                    panelData={panelData}
                    onEditingStateChange={setIsAnyItemEditing}
                />
            )}
            {tableName === 'vac_schedule_table_vs' && (
                <VacTruckSidePanel
                    isOpen={isOpen}
                    rowData={panelData?.rowData ?? null}
                    onClose={handleClose}
                />
            )}
            {tableName === 'custom_column' && (
                <ColumnConfigSidePanel
                    isOpen={isOpen}
                    panelData={panelData}
                    columnConfigs={columnConfigs}
                    tableName={sourceTableName}
                />
            )}
        </div>
    );
};

export default TableSidePanel;
